// Centralized API client with Supabase auth integration
import { getAccessToken } from './authBootstrap';
import API_BASE from './apiBase';
import { dbg } from './dbg';

// Types inlined to avoid circular dependency with campaign-creation
interface LegacyMediaSelection {
  packId: number;
  formats: Array<{ formatId: number; quantity: number }>;
}
interface FlatMediaSelection {
  mediaFormatId: number;
  quantity: number;
  storeId?: number;
}
interface StoreSelection {
  storeId: number;
  [key: string]: unknown;
}

// Re-export API_BASE so call sites can import from '@/lib/api'
export { API_BASE };

export async function withAuthHeaders(init: RequestInit = {}): Promise<RequestInit> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };

  // Preserve any existing Authorization header
  if (!('Authorization' in headers) && !('authorization' in headers)) {
    const token = await getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      // Dev-only: log token attachment
      try {
        const isDev = !!(import.meta as any)?.env?.DEV;
        if (isDev) {
          console.log('[API_FETCH] Attaching Supabase token: present ✅');
        }
      } catch { }
    } else {
      // Log missing token in both dev and prod for debugging
      try {
        console.warn('[API_FETCH] No Supabase token available - request will be unauthorized');
      } catch { }
    }
  }

  return { ...init, headers };
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const isAbs = /^https?:\/\//i.test(path);
  // Final URL building: `${API_BASE}${path}`; ensure path has a single leading '/'
  const normalizedPath = isAbs ? path : (path.startsWith('/') ? path : `/${path}`);
  const url = isAbs ? normalizedPath : `${API_BASE}${normalizedPath}`;

  dbg.log('[API_URL_DEBUG]', { API_BASE, path, final_url: url });

  // Import supabase client for direct session access
  const { supabase } = await import('./authBootstrap');

  // Get fresh session and require token for all API calls
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error("[API] Supabase session error:", error.message);
    throw new Error("Authentication error: " + error.message);
  }
  if (!session?.access_token) {
    // Don't redirect immediately - log and try getAccessToken fallback
    console.warn("[API] No session from getSession(), trying getAccessToken fallback");
    const fallbackToken = await getAccessToken();
    if (fallbackToken) {
      console.log("[API] Fallback token retrieved successfully");
      // Build headers and continue with fallback token
      const headers = new Headers(init.headers || {});
      if (!headers.has('Authorization') && !headers.has('authorization')) {
        headers.set("Authorization", `Bearer ${fallbackToken}`);
      }
      dbg.log('[API_DEBUG] Calling (fallback)', url, 'with auth token present');
      const merged: RequestInit = { credentials: 'omit', ...init, headers };
      return fetch(url, merged);
    }
    // Only now throw - let the 401 response handler deal with redirect
    console.error("[API] No token available from any source");
    throw new Error("Unauthenticated: missing Supabase token");
  }

  // Build headers with required Authorization
  const headers = new Headers(init.headers || {});
  if (!headers.has('Authorization') && !headers.has('authorization')) {
    headers.set("Authorization", `Bearer ${session.access_token}`);

    // Dev-only: log token attachment
    try {
      const isDev = !!(import.meta as any)?.env?.DEV;
      if (isDev) {
        console.log('[API_FETCH] Attaching Supabase token: present ✅');
      }
    } catch { }
  }

  dbg.log('[API_DEBUG] Calling', url, 'with auth token present');

  // Always credentials: 'omit' for API calls
  const merged: RequestInit = {
    credentials: 'omit',
    ...init,
    headers
  };

  return fetch(url, merged);
}

export async function api(path: string, init: RequestInit = {}) { return apiFetch(path, init); }

export async function apiJson<T = unknown>(path: string, init: RequestInit = {}) {
  const { headers, ...restInit } = init;
  const res = await apiFetch(path, {
    ...restInit,
    headers: { 'Content-Type': 'application/json', ...(headers || {}) },
  });

  if (res.status === 401) {
    dbg.log(`[QUERY_DEBUG] Fetch failed for ${path} with status 401 (unauthorized)`);
    // Supabase will handle auth state - just redirect to login
    try {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    } catch { }
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    // Try to get error details from response body
    let errorDetails = '';
    let errorBody: any = null;
    try {
      errorBody = await res.json();
      errorDetails = errorBody.error || errorBody.message || JSON.stringify(errorBody);
      console.error(`[API_ERROR] ${path} failed:`, errorBody);
    } catch {
      errorDetails = await res.text().catch(() => 'Could not read error');
      console.error(`[API_ERROR] ${path} failed with status ${res.status}:`, errorDetails);
    }
    dbg.log(`[QUERY_DEBUG] Fetch failed for ${path} with status ${res.status}: ${errorDetails}`);

    // For 409 conflicts, preserve the full error body so callers can access sentAt, etc.
    if (res.status === 409 && errorBody) {
      const error = new Error(`${path} failed: ${res.status} - ${errorDetails}`) as any;
      error.status = 409;
      error.body = errorBody;
      throw error;
    }

    throw new Error(`${path} failed: ${res.status} - ${errorDetails}`);
  }
  dbg.log(`[QUERY_DEBUG] Fetch successful for ${path}`);
  return (await res.json()) as T;
}

// Convenience helper for campaign creation that expects { campaign, snapshot }
export interface CreateCampaignPayload {
  campaign: Record<string, unknown>;
  retailers?: number[];
  stores?: Array<number | StoreSelection>;
  packs?: number[];
  media?: Array<LegacyMediaSelection | FlatMediaSelection>;
  groups?: unknown[]; // Store format groups for manual-groups flow
}

export async function createCampaign(data: CreateCampaignPayload) {
  const payload: CreateCampaignPayload = {
    campaign: data.campaign,
    retailers: Array.isArray(data.retailers) ? data.retailers : [],
    stores: Array.isArray(data.stores) ? data.stores : [],
    packs: Array.isArray(data.packs) ? data.packs : [],
    media: Array.isArray(data.media) ? data.media : [],
  };
  
  // Include groups if present (for manual-groups flow)
  if (Array.isArray(data.groups) && data.groups.length > 0) {
    payload.groups = data.groups;
    console.log('[createCampaign] Including groups in payload:', data.groups.length, 'groups');
  }

  try { if (import.meta.env.DEV) console.log('[createCampaign] Payload', payload); } catch { }

  const res = await apiFetch('/api/campaigns/with-relationships', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`createCampaign failed: ${res.status} ${txt}`);
  }
  return res.json();
}

export async function listCampaignSnapshots() {
  const res = await apiFetch('/api/campaign-snapshots');
  if (!res.ok) throw new Error(`/api/campaign-snapshots failed: ${res.status}`);
  return res.json();
}

export async function listCycleSnapshots() {
  const res = await apiFetch('/api/locked-cycles');
  if (!res.ok) throw new Error(`/api/locked-cycles failed: ${res.status}`);
  return res.json();
}

export async function apiBlob(path: string, init: RequestInit = {}): Promise<{ blob: Blob; filename?: string }> {
  dbg.log("[PDF_DEBUG] apiBlob: Fetching", path);
  const res = await apiFetch(path, init);
  dbg.log("[PDF_DEBUG] apiBlob: Response status", res.status, "ok:", res.ok);

  if (res.status === 401) {
    console.error("[PDF_DIAGNOSIS] Failure: missing auth token (401)");
    // Supabase will handle auth state - just redirect to login
    try {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    } catch { }
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    const errorText = await res.text().catch(() => 'Could not read error response');
    console.error("[PDF_DEBUG] apiBlob: Request failed", res.status, errorText.substring(0, 200));

    // One-line diagnosis
    if (res.status === 404) {
      console.error("[PDF_DIAGNOSIS] Failure: locked cycle not found (404)");
    } else if (res.status === 500) {
      console.error("[PDF_DIAGNOSIS] Failure: server error generating PDF (500)");
    } else {
      console.error("[PDF_DIAGNOSIS] Failure: HTTP", res.status, res.statusText);
    }

    throw new Error(`${path} failed: ${res.status} - ${errorText}`);
  }
  const blob = await res.blob();
  if (import.meta.env.DEV) console.log("[PDF_DEBUG] apiBlob: blob size:", blob.size, "bytes");

  // Success/failure diagnosis for blob
  if (blob.size === 0) {
    console.error("[PDF_DIAGNOSIS] Failure: empty blob returned");
  } else {
    if (import.meta.env.DEV) console.log("[PDF_DIAGNOSIS] Success: PDF blob size", blob.size, "bytes");
  }

  const disposition = res.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename="([^"]+)"/);
  const filename = filenameMatch?.[1];

  return { blob, filename };
}

// Install a global fetch interceptor to inject Authorization for protected API requests
export function installApiAuthInterceptor(): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const originalFetch = window.fetch.bind(window);
  // Avoid double-install
  if ((window as any).__apiAuthInterceptorInstalled) return;
  (window as any).__apiAuthInterceptorInstalled = true;

  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const isRequestObj = typeof Request !== 'undefined' && input instanceof Request;
      const urlStr = isRequestObj ? (input as Request).url : (typeof input === 'string' ? input : (input as URL).toString?.() ?? '');

      // Determine if request should be treated as protected
      const devEnv = (() => { try { return !!(import.meta as any)?.env?.DEV; } catch { return false; } })();
      const apiBaseEnv = (() => { try { return (import.meta as any)?.env?.VITE_API_BASE as string | undefined; } catch { return undefined; } })();
      const apiBaseOrigin = (() => {
        try {
          if (!apiBaseEnv) return undefined;
          const u = new URL(apiBaseEnv);
          return u.origin;
        } catch { return undefined; }
      })();

      const isAbsolute = /^https?:\/\//i.test(urlStr);
      const isApiPath = urlStr.startsWith('/api/');
      const isSameOriginApi = !isAbsolute && isApiPath;
      const isEnvApiOrigin = (() => {
        if (!isAbsolute || !apiBaseOrigin) return false;
        try { return new URL(urlStr).origin === apiBaseOrigin; } catch { return false; }
      })();
      const shouldAttach = isSameOriginApi || isEnvApiOrigin;

      try {
        dbg.log('[AUTH_INTERCEPT:decision]', { url: urlStr, isAbsolute, isApiPath, isSameOriginApi, apiBaseOrigin: apiBaseOrigin || '(unset)', isEnvApiOrigin, shouldAttach });
      } catch { }


      if (shouldAttach) {
        const baseHeaders = isRequestObj ? (input as Request).headers : undefined;
        const headers = new Headers(baseHeaders || init?.headers || {});
        const hadAuthorization = headers.has('Authorization');

        // NEVER override existing Authorization header
        if (!hadAuthorization) {
          const token = await getAccessToken();
          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
            try {
              dbg.log('[AUTH_INTERCEPT:attach]', { url: urlStr, tokenFound: true });
            } catch { }
            // Dev-only: log once per page load when attaching a token.
            try {
              // @ts-ignore
              const g: any = window as any;
              if (!g.__authAttachLogged && devEnv) {
                g.__authAttachLogged = true;
                console.debug('[API_FETCH] Attaching Supabase token: present ✅ (interceptor)');
              }
            } catch { }
          } else {
            try {
              dbg.log('[AUTH_INTERCEPT:attach]', { url: urlStr, tokenFound: false });
            } catch { }
            // Log missing token in production too for debugging
            try {
              console.warn('[API_FETCH] No Supabase token available - request will be unauthorized (interceptor)');
            } catch { }
          }
        }

        const merged: RequestInit = {
          ...init,
          headers,
          // Always omit credentials; header-only auth
          credentials: 'omit',
        };
        return originalFetch(isRequestObj ? new Request((input as Request), merged) : urlStr, merged);
      }
    } catch { }
    return originalFetch(input as any, init);
  }) as any;
}

// Lightweight dev helper to confirm Authorization header is present server-side
export async function checkAuthHeader(): Promise<void> {
  try {
    // @ts-ignore
    const devEnv = !!((import.meta as any)?.env?.DEV);
    if (!devEnv) return;
    const res = await fetch('/api/_debug/token', { method: 'GET', credentials: 'omit' });
    const json = await res.json().catch(() => null);
    console.debug('api._debug.token', json);
  } catch { }
}

// Supabase auth exports
export { getAccessToken, getAuthReady } from './authBootstrap';
